import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import useStore from '../store/useStore';
import CherryMark, { PetalField } from '../components/CherryMark';
import Faq from '../components/Faq';
import useScrollReveal from '../hooks/useScrollReveal';
import { orchard } from '../theme';

const copy = {
  en: {
    navHow: 'How it works',
    navProduct: 'Inside',
    navFeatures: 'Features',
    navFaq: 'FAQ',
    forBusiness: 'For business',
    signIn: 'Sign in',
    eyebrow: 'Student profiler for language studios',
    heroA: 'Every student,',
    heroB: 'hand-picked.',
    heroSub:
      'Cherry-Pick CORE gathers lessons, flashcards and attendance into one living profile — so teachers teach to the child in front of them, and parents watch real growth.',
    ctaPrimary: 'Try the live demo',
    ctaPrimaryBusy: 'Opening the demo…',
    ctaDemoHint: 'No sign-up — a real cabinet with sample students.',
    ctaSecondary: 'See it white-labeled',
    trustLine:
      'Built inside a working kids’ English studio — proven on real lessons, not in a lab.',
    statCard: 'Reading · this month',
    statValue: '+38%',
    seasonEyebrow: 'A season, not a semester',
    steps: [
      {
        n: 'Plant',
        title: 'Start a profile',
        text: 'A student joins with a photo, a level and a goal. Two minutes, and the orchard has one more tree.',
      },
      {
        n: 'Tend',
        title: 'Track every lesson',
        text: 'Theme assessments, flashcards, attendance and notes flow into the profile as you teach — nothing extra to fill in at night.',
      },
      {
        n: 'Harvest',
        title: 'Share the growth',
        text: 'One tap builds a bilingual PDF report with charts parents actually read — and every number framed as progress.',
      },
    ],
    productEyebrow: 'Inside the platform',
    productTitle: 'One profile instead of ten spreadsheets',
    productSub:
      'Themes, attendance and notes live on one page. This is the actual interface — the demo opens on the same data.',
    productThemes: [
      { name: 'Animals · Животные', pct: 92 },
      { name: 'Colors · Цвета', pct: 75 },
      { name: 'Family · Семья', pct: 60 },
      { name: 'Food · Еда', pct: 38 },
    ],
    productStudentName: 'Alisa Morozova',
    productStudentMeta: '6 y.o. · Movers · Pre-A1',
    productAttendance: 'Attendance · 8 weeks',
    productReport: 'May report · sent to parents',
    productNote: '“Built the phrase «I see a fox» on her own — first time.”',
    productNoteMeta: 'Lesson note · Tuesday',
    productLink: 'Open this profile in the demo',
    featuresEyebrow: 'What grows inside',
    featureMain: {
      title: 'Bilingual PDF reports',
      text: 'English and Russian in one document, charts drawn in your brand. One tap — and the Friday report is in the parents’ chat.',
    },
    featureMainChip: 'Report · May',
    features: [
      {
        title: 'Living profiles',
        text: 'Timeline, portfolio, milestones — the whole journey on one page.',
      },
      {
        title: 'Theme assessments',
        text: 'Versioned templates test exactly what was taught.',
      },
      {
        title: 'Attendance intelligence',
        text: 'Calendars connect showing up with speaking up.',
      },
      {
        title: 'Parent portal',
        text: 'A gentle, readable view of the child’s season.',
      },
      {
        title: 'AI assistant',
        text: 'Drafts lesson notes, spots who needs a different approach.',
      },
    ],
    philosophyEyebrow: 'Our one rule',
    philosophy: 'A report should never make a family feel smaller.',
    philosophyText:
      'CORE frames every metric as growth. No red marks, no rankings against other children — only the distance a student has travelled and the branch that is next to bloom.',
    parentsEyebrow: 'For parents',
    parentsTitle: 'The Friday report they look forward to',
    parentsText:
      'Charts a grandmother can read, in the language she prefers. Sent from your studio, signed with your name.',
    faqEyebrow: 'Questions, answered',
    faq: [
      {
        q: 'How much teacher time does it take?',
        a: 'A couple of minutes during the lesson. Scores are taps on a 0–1–2 scale per word or phrase — not essays written at midnight. Notes are optional and the AI assistant drafts them for you.',
      },
      {
        q: 'Do parents need to install anything?',
        a: 'No. Parents get a browser portal and a PDF report that opens in any messenger. A grandmother with an old phone sees the same charts.',
      },
      {
        q: 'Are reports really bilingual?',
        a: 'Yes — English and Russian side by side in one document, so both the teacher’s wording and the family’s language are respected.',
      },
      {
        q: 'What if a child misses classes?',
        a: 'Attendance is tracked and factored in, but the report stays supportive: it shows the path travelled, never a red mark. That rule is built into the product.',
      },
      {
        q: 'Can I import my existing student list?',
        a: 'Yes — students import from Excel. Templates are yours to build, so the platform adapts to how you already teach.',
      },
      {
        q: 'Is it only for English?',
        a: 'No. Templates are free-form themes with words and phrases — any language or subject that is taught in themes works.',
      },
    ],
    ctaTitle: 'Bring your studio into season.',
    ctaText:
      'CORE runs the profiles, the reports and the parent portal — you keep the teaching.',
    stickyCta: 'Try the demo',
  },
  ru: {
    navHow: 'Как работает',
    navProduct: 'Внутри',
    navFeatures: 'Возможности',
    navFaq: 'Вопросы',
    forBusiness: 'Для бизнеса',
    signIn: 'Войти',
    eyebrow: 'Платформа профилей для языковых студий',
    heroA: 'Каждый ученик',
    heroB: '— на особом счету.',
    heroSub:
      'Cherry-Pick CORE собирает уроки, карточки и посещаемость в живой профиль — учитель видит ребёнка, а родители видят настоящий рост.',
    ctaPrimary: 'Попробовать демо',
    ctaPrimaryBusy: 'Открываем демо…',
    ctaDemoHint: 'Без регистрации — настоящий кабинет с примерами учеников.',
    ctaSecondary: 'Под вашим брендом',
    trustLine:
      'Создано внутри действующей детской студии английского — обкатано на настоящих уроках, а не в лаборатории.',
    statCard: 'Чтение · за месяц',
    statValue: '+38%',
    seasonEyebrow: 'Сезон, а не семестр',
    steps: [
      {
        n: 'Посадить',
        title: 'Создайте профиль',
        text: 'Ученик приходит с фото, уровнем и целью. Две минуты — и в саду новое дерево.',
      },
      {
        n: 'Растить',
        title: 'Отмечайте каждый урок',
        text: 'Оценки по темам, карточки, посещаемость и заметки попадают в профиль прямо во время урока — ничего не нужно заполнять ночью.',
      },
      {
        n: 'Собрать',
        title: 'Поделитесь ростом',
        text: 'Одно нажатие — и готов двуязычный PDF-отчёт с графиками, которые родители действительно читают.',
      },
    ],
    productEyebrow: 'Внутри платформы',
    productTitle: 'Один профиль вместо десяти таблиц',
    productSub:
      'Темы, посещаемость и заметки живут на одной странице. Это настоящий интерфейс — демо откроется на этих же данных.',
    productThemes: [
      { name: 'Animals · Животные', pct: 92 },
      { name: 'Colors · Цвета', pct: 75 },
      { name: 'Family · Семья', pct: 60 },
      { name: 'Food · Еда', pct: 38 },
    ],
    productStudentName: 'Алиса Морозова',
    productStudentMeta: '6 лет · Movers · Pre-A1',
    productAttendance: 'Посещаемость · 8 недель',
    productReport: 'Отчёт за май · отправлен родителям',
    productNote: '«Сама построила фразу “I see a fox” — впервые».',
    productNoteMeta: 'Заметка с урока · вторник',
    productLink: 'Открыть этот профиль в демо',
    featuresEyebrow: 'Что растёт внутри',
    featureMain: {
      title: 'Двуязычные PDF-отчёты',
      text: 'Английский и русский в одном документе, графики в вашем стиле. Одно нажатие — и пятничный отчёт уже в чате у родителей.',
    },
    featureMainChip: 'Отчёт · Май',
    features: [
      {
        title: 'Живые профили',
        text: 'Таймлайн, портфолио, достижения — весь путь на одной странице.',
      },
      {
        title: 'Оценки по темам',
        text: 'Версионные шаблоны проверяют ровно то, что пройдено.',
      },
      {
        title: 'Аналитика посещаемости',
        text: 'Календари связывают присутствие с прогрессом.',
      },
      {
        title: 'Портал для родителей',
        text: 'Понятная и тёплая картина сезона ребёнка.',
      },
      {
        title: 'AI-ассистент',
        text: 'Пишет черновики заметок, замечает, кому нужен другой подход.',
      },
    ],
    philosophyEyebrow: 'Наше правило',
    philosophy: 'Отчёт не должен принижать семью.',
    philosophyText:
      'CORE подаёт каждую цифру как рост. Без красных отметок и сравнений с другими детьми — только путь, который прошёл ученик, и ветка, которая зацветёт следующей.',
    parentsEyebrow: 'Для родителей',
    parentsTitle: 'Пятничный отчёт, которого ждут',
    parentsText:
      'Графики, понятные даже бабушке, на удобном ей языке. Отправлено от имени вашей студии.',
    faqEyebrow: 'Частые вопросы',
    faq: [
      {
        q: 'Сколько времени это отнимает у учителя?',
        a: 'Пару минут прямо на уроке. Оценки — это касания по шкале 0–1–2 для каждого слова или фразы, а не сочинения по ночам. Заметки необязательны, а черновики пишет AI-ассистент.',
      },
      {
        q: 'Нужно ли родителям что-то устанавливать?',
        a: 'Нет. У родителей — портал в браузере и PDF-отчёт, который открывается в любом мессенджере. Бабушка со старым телефоном увидит те же графики.',
      },
      {
        q: 'Отчёты правда на двух языках?',
        a: 'Да — английский и русский рядом в одном документе. Уважаем и формулировки учителя, и язык семьи.',
      },
      {
        q: 'Что если ребёнок пропускает занятия?',
        a: 'Посещаемость учитывается, но отчёт остаётся поддерживающим: он показывает пройденный путь, а не красные отметки. Это правило зашито в продукт.',
      },
      {
        q: 'Можно ли перенести существующий список учеников?',
        a: 'Да — ученики импортируются из Excel. Шаблоны вы собираете сами, поэтому платформа подстраивается под то, как вы уже преподаёте.',
      },
      {
        q: 'Это только для английского?',
        a: 'Нет. Шаблоны — это свободные темы со словами и фразами: подойдёт любой язык или предмет, который преподаётся по темам.',
      },
    ],
    ctaTitle: 'Пусть ваша студия расцветёт.',
    ctaText:
      'CORE ведёт профили, отчёты и портал родителей — за вами остаётся преподавание.',
    stickyCta: 'Попробовать демо',
  },
};

const eyebrowSx = {
  fontFamily: orchard.fontMono,
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: orchard.cherry,
  display: 'block',
  mb: 2,
};

const navLinkSx = {
  fontSize: 14,
  fontWeight: 600,
  color: 'text.secondary',
  textDecoration: 'none',
  px: 1,
  py: 0.5,
  borderRadius: 1,
  transition: 'color 0.18s ease',
  '&:hover': { color: orchard.cherry },
};

/* Attendance dot grid — 8 weeks × 2 lessons, mostly green */
const ATT = [
  1, 1, 1, 2, 1, 1, 0, 1, 1, 1, 1, 1, 2, 1, 1, 1,
]; /* 1 present, 2 late, 0 absent */

function AttendanceDots() {
  const color = (v) =>
    v === 1 ? orchard.leaf : v === 2 ? orchard.honey : orchard.blossom;
  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.75 }}
    >
      {ATT.map((v, i) => (
        <Box
          key={i}
          sx={{
            width: '100%',
            aspectRatio: '1',
            maxWidth: 14,
            borderRadius: '50%',
            bgcolor: color(v),
            opacity: v === 0 ? 0.7 : 1,
          }}
        />
      ))}
    </Box>
  );
}

/* Browser-framed product mock — the real UI, hand-built, bars grow on reveal */
function ProductMock({ t }) {
  const barColors = [orchard.leaf, orchard.cherry, orchard.honey, orchard.leaf];
  return (
    <Box
      sx={{
        borderRadius: 5,
        overflow: 'hidden',
        border: '1px solid rgba(36,30,43,0.1)',
        boxShadow: '0 44px 88px -36px rgba(140,18,48,0.3)',
        bgcolor: '#fff',
      }}
    >
      {/* Browser bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 2,
          py: 1.25,
          borderBottom: '1px solid rgba(36,30,43,0.08)',
          bgcolor: 'rgba(36,30,43,0.02)',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: 'rgba(36,30,43,0.12)',
            }}
          />
        ))}
        <Typography
          sx={{
            ml: 1.5,
            fontFamily: orchard.fontMono,
            fontSize: 11.5,
            color: 'text.secondary',
          }}
        >
          core.klicka.ru/student/alisa
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1.25fr 1fr' },
          gap: 3,
        }}
      >
        {/* Left: student + theme bars */}
        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: orchard.blossom,
                color: orchard.cherryDeep,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 17,
                flexShrink: 0,
              }}
            >
              {t.productStudentName.charAt(0)}
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: 15.5, lineHeight: 1.2 }}
              >
                {t.productStudentName}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t.productStudentMeta}
              </Typography>
            </Box>
          </Box>

          {t.productThemes.map((theme, i) => (
            <Box key={theme.name} sx={{ mb: 1.75 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {theme.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: orchard.fontMono,
                    fontSize: 12,
                    fontWeight: 700,
                    color: barColors[i],
                  }}
                >
                  {theme.pct}%
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 99,
                  bgcolor: 'rgba(36,30,43,0.06)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  className="cp-bar"
                  style={{ '--cp-bar-w': `${theme.pct}%` }}
                  sx={{
                    height: '100%',
                    borderRadius: 99,
                    bgcolor: barColors[i],
                    transitionDelay: `${0.25 + i * 0.12}s`,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Right: attendance + report + note */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(36,30,43,0.08)',
            }}
          >
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 10.5,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 1.25,
              }}
            >
              {t.productAttendance}
            </Typography>
            <AttendanceDots />
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(46,125,91,0.07)',
              border: `1px dashed rgba(46,125,91,0.4)`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: orchard.leaf,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: orchard.leaf }}
            >
              {t.productReport}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(36,30,43,0.08)',
              bgcolor: 'rgba(232,169,61,0.06)',
            }}
          >
            <Typography
              sx={{ fontSize: 13, lineHeight: 1.55, fontStyle: 'italic' }}
            >
              {t.productNote}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontFamily: orchard.fontMono,
                fontSize: 10.5,
                color: 'text.secondary',
              }}
            >
              {t.productNoteMeta}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* Mini bilingual report page — visual for the big bento cell */
function ReportMini({ t }) {
  return (
    <Box
      sx={{
        mt: 3,
        mr: { xs: -2, sm: -5 },
        mb: { xs: -3, sm: -6 },
        ml: 'auto',
        maxWidth: 340,
        bgcolor: '#fff',
        border: '1px solid rgba(36,30,43,0.12)',
        borderRadius: '14px 14px 0 0',
        boxShadow: '0 -18px 44px -24px rgba(140,18,48,0.3)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: orchard.cherry,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 12 }}>
          {t.featureMainChip}
        </Typography>
        <Typography
          sx={{
            fontFamily: orchard.fontMono,
            color: 'rgba(255,255,255,0.85)',
            fontSize: 10,
          }}
        >
          RU · EN
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {[86, 64, 52].map((w, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}
          >
            <Box
              sx={{
                height: 7,
                width: 46,
                borderRadius: 99,
                bgcolor: 'rgba(36,30,43,0.1)',
              }}
            />
            <Box
              sx={{
                flexGrow: 1,
                height: 7,
                borderRadius: 99,
                bgcolor: 'rgba(36,30,43,0.06)',
                overflow: 'hidden',
              }}
            >
              <Box
                className="cp-bar"
                style={{ '--cp-bar-w': `${w}%` }}
                sx={{
                  height: '100%',
                  borderRadius: 99,
                  bgcolor: [orchard.leaf, orchard.cherry, orchard.honey][i],
                  transitionDelay: `${0.35 + i * 0.15}s`,
                }}
              />
            </Box>
          </Box>
        ))}
        <Typography
          sx={{
            mt: 1.5,
            fontFamily: orchard.fontMono,
            fontSize: 10,
            color: orchard.leaf,
            fontWeight: 700,
          }}
        >
          ▲ +24 words · слова за сезон
        </Typography>
      </Box>
    </Box>
  );
}

export default function LandingPage() {
  const { language, setLanguage, login } = useStore();
  const t = copy[language] || copy.en;
  const rootRef = useScrollReveal();
  const navigate = useNavigate();
  const [demoBusy, setDemoBusy] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const heroRef = useRef(null);
  const ctaRef = useRef(null);

  async function enterDemo() {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      const res = await fetch('/api/auth/demo', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        login(data.role, data.user);
        navigate('/');
        return;
      }
    } catch (err) {
      /* server unreachable — fall through to reset */
    }
    setDemoBusy(false);
  }

  /* Sticky mobile CTA: visible after hero leaves, hidden at the final band */
  useEffect(() => {
    const hero = heroRef.current;
    const cta = ctaRef.current;
    if (!hero || !cta || typeof IntersectionObserver === 'undefined') return;
    let heroVisible = true;
    let ctaVisible = false;
    const update = () => setShowSticky(!heroVisible && !ctaVisible);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.target === hero) heroVisible = e.isIntersecting;
          if (e.target === cta) ctaVisible = e.isIntersecting;
        });
        update();
      },
      { threshold: 0.05 }
    );
    io.observe(hero);
    io.observe(cta);
    return () => io.disconnect();
  }, []);

  return (
    <Box
      ref={rootRef}
      sx={{
        bgcolor: orchard.paper,
        color: orchard.ink,
        minHeight: '100vh',
        overflowX: 'hidden',
      }}
    >
      {/* ── Top bar ── */}
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: 'rgba(251,248,245,0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid rgba(36,30,43,0.08)`,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 1.25 }}
        >
          <CherryMark size={28} />
          <Typography
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: '1rem',
              mr: 2,
            }}
          >
            Cherry-Pick{' '}
            <Box component="span" sx={{ color: orchard.cherry }}>
              CORE
            </Box>
          </Typography>
          <Box
            component="nav"
            aria-label="Sections"
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 0.5,
              mr: 'auto',
            }}
          >
            <Box component="a" href="#how" sx={navLinkSx}>
              {t.navHow}
            </Box>
            <Box component="a" href="#product" sx={navLinkSx}>
              {t.navProduct}
            </Box>
            <Box component="a" href="#features" sx={navLinkSx}>
              {t.navFeatures}
            </Box>
            <Box component="a" href="#faq" sx={navLinkSx}>
              {t.navFaq}
            </Box>
          </Box>
          <Box sx={{ mr: { xs: 'auto', md: 0 } }} />
          <Button
            size="small"
            startIcon={<TranslateIcon />}
            onClick={() => setLanguage(language === 'en' ? 'ru' : 'en')}
            sx={{ color: 'text.secondary' }}
          >
            {(language || 'en').toUpperCase()}
          </Button>
          <Button
            size="small"
            component={RouterLink}
            to="/login"
            sx={{
              color: 'text.secondary',
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            {t.signIn}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={enterDemo}
            disabled={demoBusy}
          >
            {t.ctaPrimary}
          </Button>
        </Container>
      </Box>

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative' }} ref={heroRef}>
        <PetalField />
        <Container
          maxWidth="lg"
          sx={{
            pt: { xs: 7, md: 11 },
            pb: { xs: 8, md: 12 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
            gap: { xs: 6, md: 8 },
            alignItems: 'center',
          }}
        >
          <Box className="cp-stagger">
            <Typography component="span" sx={eyebrowSx}>
              {t.eyebrow}
            </Typography>
            <Typography
              component="h1"
              className="cp-balance"
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 700,
                fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.3rem' },
                lineHeight: 1.12,
                letterSpacing: '-0.02em',
              }}
            >
              {t.heroA}
              <br />
              <Box
                component="span"
                sx={{
                  color: orchard.cherry,
                  position: 'relative',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.heroB}
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    right: -38,
                    top: -6,
                    display: { xs: 'none', md: 'inline-block' },
                    animation: 'cpCherryBob 2.6s ease-in-out infinite',
                    lineHeight: 0,
                  }}
                >
                  <CherryMark size={34} />
                </Box>
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 3,
                fontSize: '1.1rem',
                lineHeight: 1.65,
                color: 'text.secondary',
                maxWidth: 520,
              }}
            >
              {t.heroSub}
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={enterDemo}
                disabled={demoBusy}
                endIcon={<PlayArrowIcon />}
              >
                {demoBusy ? t.ctaPrimaryBusy : t.ctaPrimary}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/partners"
              >
                {t.ctaSecondary}
              </Button>
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
              {t.ctaDemoHint}
            </Typography>
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                maxWidth: 460,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  alignSelf: 'stretch',
                  borderRadius: 2,
                  bgcolor: orchard.leaf,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: 'text.secondary',
                  lineHeight: 1.5,
                }}
              >
                {t.trustLine}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: 'relative' }} className="cp-reveal">
            <Box
              component="img"
              src="/img/hero-classroom.webp"
              alt=""
              width={1344}
              height={768}
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '28px 100px 28px 28px',
                boxShadow: '0 40px 80px -30px rgba(140,18,48,0.35)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -22,
                left: { xs: 12, md: -28 },
                bgcolor: '#fff',
                borderRadius: 3.5,
                px: 2.5,
                py: 1.75,
                boxShadow: '0 20px 45px -18px rgba(36,30,43,0.35)',
                border: '1px solid rgba(36,30,43,0.06)',
                animation: 'cpCherryBob 3.4s ease-in-out infinite',
              }}
            >
              <Typography
                sx={{
                  fontFamily: orchard.fontMono,
                  fontSize: 10.5,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                {t.statCard}
              </Typography>
              <Typography
                sx={{
                  fontFamily: orchard.fontDisplay,
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: orchard.leaf,
                }}
              >
                {t.statValue}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Season timeline ── */}
      <Box
        id="how"
        sx={{
          bgcolor: '#fff',
          borderTop: '1px solid rgba(36,30,43,0.06)',
          scrollMarginTop: 72,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.seasonEyebrow}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, md: 5 },
              mt: 2,
            }}
          >
            {t.steps.map((s, i) => (
              <Box
                key={s.title}
                className="cp-reveal"
                sx={{ transitionDelay: `${i * 0.12}s`, position: 'relative' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <CherryMark
                    size={26}
                    fruit={[orchard.blossom, orchard.honey, orchard.cherry][i]}
                    fruitB={[orchard.blossom, orchard.honey, orchard.cherry][i]}
                  />
                  <Typography
                    sx={{
                      fontFamily: orchard.fontMono,
                      fontSize: 12,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: orchard.cherry,
                    }}
                  >
                    {s.n}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: orchard.fontDisplay,
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    mt: 1.5,
                  }}
                >
                  {s.title}
                </Typography>
                <Typography
                  sx={{ mt: 1.25, color: 'text.secondary', lineHeight: 1.65 }}
                >
                  {s.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Product mock ── */}
      <Box id="product" sx={{ scrollMarginTop: 72 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.85fr 1.35fr' },
              gap: { xs: 4, md: 7 },
              alignItems: 'center',
            }}
          >
            <Box className="cp-reveal">
              <Typography component="span" sx={eyebrowSx}>
                {t.productEyebrow}
              </Typography>
              <Typography
                className="cp-balance"
                sx={{
                  fontFamily: orchard.fontDisplay,
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '1.9rem' },
                  lineHeight: 1.28,
                }}
              >
                {t.productTitle}
              </Typography>
              <Typography
                sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
              >
                {t.productSub}
              </Typography>
              <Button
                onClick={enterDemo}
                disabled={demoBusy}
                endIcon={<PlayArrowIcon />}
                sx={{ mt: 2.5, color: orchard.cherry, px: 0 }}
              >
                {t.productLink}
              </Button>
            </Box>
            <Box className="cp-reveal" sx={{ transitionDelay: '0.12s' }}>
              <ProductMock t={t} />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Features bento ── */}
      <Box
        id="features"
        sx={{
          bgcolor: '#fff',
          borderTop: '1px solid rgba(36,30,43,0.06)',
          scrollMarginTop: 72,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.featuresEyebrow}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 3,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gridAutoRows: 'minmax(0, auto)',
            }}
          >
            {/* Big cell: bilingual reports with mini report mock */}
            <Box
              className="cp-reveal"
              sx={{
                gridColumn: { xs: 'auto', sm: 'span 2', md: 'span 2' },
                gridRow: { md: 'span 2' },
                p: { xs: 3, sm: 4 },
                pb: 0,
                borderRadius: 4,
                bgcolor: orchard.paper,
                border: '1px solid rgba(36,30,43,0.08)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                transition:
                  'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.25s ease, opacity 0.7s cubic-bezier(0.22,1,0.36,1)',
                '&:hover': {
                  borderColor: 'rgba(194,35,62,0.35)',
                  boxShadow: '0 22px 44px -20px rgba(140,18,48,0.25)',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: orchard.fontDisplay,
                  fontWeight: 600,
                  fontSize: '1.35rem',
                }}
              >
                {t.featureMain.title}
              </Typography>
              <Typography
                sx={{
                  mt: 1.25,
                  color: 'text.secondary',
                  lineHeight: 1.65,
                  maxWidth: 440,
                }}
              >
                {t.featureMain.text}
              </Typography>
              <ReportMini t={t} />
            </Box>

            {t.features.map((f, i) => (
              <Box
                key={f.title}
                className="cp-reveal"
                sx={{
                  transitionDelay: `${(i % 3) * 0.1}s`,
                  p: 3.5,
                  borderRadius: 4,
                  bgcolor: orchard.paper,
                  border: '1px solid rgba(36,30,43,0.08)',
                  transition:
                    'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.25s ease, opacity 0.7s cubic-bezier(0.22,1,0.36,1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(194,35,62,0.35)',
                    boxShadow: '0 22px 44px -20px rgba(140,18,48,0.25)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.02rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: orchard.cherry,
                      flexShrink: 0,
                    }}
                  />
                  {f.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 1.25,
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  }}
                >
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Philosophy band ── */}
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 8, md: 11 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 5, md: 8 },
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src="/img/cherry-still.webp"
          alt=""
          width={1248}
          height={832}
          className="cp-reveal"
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: '100px 28px 28px 28px',
            boxShadow: '0 30px 60px -28px rgba(36,30,43,0.3)',
          }}
        />
        <Box className="cp-reveal" sx={{ transitionDelay: '0.15s' }}>
          <Typography component="span" sx={eyebrowSx}>
            {t.philosophyEyebrow}
          </Typography>
          <Typography
            className="cp-balance"
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '1.9rem' },
              lineHeight: 1.3,
            }}
          >
            {t.philosophy}
          </Typography>
          <Typography
            sx={{ mt: 2.5, color: 'text.secondary', lineHeight: 1.7 }}
          >
            {t.philosophyText}
          </Typography>
        </Box>
      </Container>

      {/* ── Parents ── */}
      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid rgba(36,30,43,0.06)' }}>
        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 8, md: 11 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 5, md: 8 },
            alignItems: 'center',
          }}
        >
          <Box className="cp-reveal">
            <Typography component="span" sx={eyebrowSx}>
              {t.parentsEyebrow}
            </Typography>
            <Typography
              className="cp-balance"
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '1.9rem' },
                lineHeight: 1.3,
              }}
            >
              {t.parentsTitle}
            </Typography>
            <Typography
              sx={{ mt: 2.5, color: 'text.secondary', lineHeight: 1.7 }}
            >
              {t.parentsText}
            </Typography>
          </Box>
          <Box
            component="img"
            src="/img/parent-report.webp"
            alt=""
            width={1184}
            height={864}
            className="cp-reveal"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              borderRadius: '28px 28px 28px 100px',
              boxShadow: '0 30px 60px -28px rgba(36,30,43,0.35)',
              transitionDelay: '0.15s',
            }}
          />
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box id="faq" sx={{ scrollMarginTop: 72 }}>
        <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 } }}>
          <Typography component="span" sx={eyebrowSx} className="cp-reveal">
            {t.navFaq}
          </Typography>
          <Typography
            className="cp-reveal cp-balance"
            sx={{
              fontFamily: orchard.fontDisplay,
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '1.9rem' },
              lineHeight: 1.3,
              mb: 4,
            }}
          >
            {t.faqEyebrow}
          </Typography>
          <Box className="cp-reveal" sx={{ transitionDelay: '0.1s' }}>
            <Faq items={t.faq} />
          </Box>
        </Container>
      </Box>

      {/* ── CTA band ── */}
      <Box sx={{ bgcolor: orchard.night, position: 'relative' }} ref={ctaRef}>
        <PetalField tint="rgba(242,89,114,0.25)" />
        <Container
          maxWidth="md"
          sx={{ py: { xs: 9, md: 12 }, textAlign: 'center' }}
        >
          <Box className="cp-reveal">
            <Box sx={{ display: 'inline-block', mb: 2 }}>
              <CherryMark
                size={46}
                fruit={orchard.cherryBright}
                fruitB={orchard.cherryBright}
                stem={orchard.leafBright}
                leaf={orchard.leafBright}
              />
            </Box>
            <Typography
              className="cp-balance"
              sx={{
                fontFamily: orchard.fontDisplay,
                fontWeight: 700,
                fontSize: { xs: '1.7rem', md: '2.3rem' },
                color: '#F4EEF6',
                lineHeight: 1.25,
              }}
            >
              {t.ctaTitle}
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: 'rgba(244,238,246,0.7)',
                fontSize: '1.05rem',
                lineHeight: 1.65,
              }}
            >
              {t.ctaText}
            </Typography>
            <Box
              sx={{
                mt: 4,
                display: 'flex',
                gap: 1.5,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={enterDemo}
                disabled={demoBusy}
                endIcon={<PlayArrowIcon />}
              >
                {demoBusy ? t.ctaPrimaryBusy : t.ctaPrimary}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/partners"
                sx={{
                  color: '#F4EEF6',
                  borderColor: 'rgba(244,238,246,0.4)',
                  '&:hover': { borderColor: '#F4EEF6' },
                }}
              >
                {t.forBusiness}
              </Button>
            </Box>
            <Typography
              sx={{ mt: 1.75, fontSize: 13, color: 'rgba(244,238,246,0.55)' }}
            >
              {t.ctaDemoHint}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ bgcolor: orchard.night, pb: 4 }}>
        <Container
          maxWidth="lg"
          sx={{
            borderTop: '1px solid rgba(244,238,246,0.1)',
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <CherryMark size={20} fruit={orchard.cherryBright} />
          <Typography sx={{ color: 'rgba(244,238,246,0.55)', fontSize: 13 }}>
            © {new Date().getFullYear()} Cherry-Pick CORE · made with love by{' '}
            <Box
              component="a"
              href="https://klicka.ru"
              target="_blank"
              rel="noreferrer"
              sx={{ color: 'inherit' }}
            >
              klicka.ru
            </Box>
          </Typography>
        </Container>
      </Box>

      {/* ── Sticky mobile CTA ── */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          display: { xs: 'block', md: 'none' },
          p: 1.5,
          pb: 'calc(12px + env(safe-area-inset-bottom))',
          bgcolor: 'rgba(251,248,245,0.92)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(36,30,43,0.1)',
          boxShadow: '0 -10px 30px -12px rgba(36,30,43,0.2)',
          transform: showSticky ? 'translateY(0)' : 'translateY(110%)',
          transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={enterDemo}
          disabled={demoBusy}
          endIcon={<PlayArrowIcon />}
        >
          {demoBusy ? t.ctaPrimaryBusy : t.stickyCta}
        </Button>
      </Box>
    </Box>
  );
}
